import * as Toggle from "@radix-ui/react-toggle";
import { Decimal } from "decimal.js";
import React, { Component } from "react";
import { FormattedMessage, type IntlShape, injectIntl, type MessageDescriptor } from "react-intl";
import { type ConnectedProps, connect } from "react-redux";
import { NavLink, type RouteComponentProps } from "react-router-dom";
import Select, { components } from "react-select";
import makeAnimated from "react-select/animated";

import { getDoctorsPays, getDoctorsTasks } from "~/actions/dashboard";
import { doctorPaymentBatch } from "~/actions/invoice";
import { PAYMENT_SYSTEM_TYPE, PaymentSystem, SBERBANK_DISCLAIMER } from "~/common/constants";
import { remoteLog } from "~/common/logging";
import { redirectTo } from "~/common/navigation";
import { isDevelopment } from "~/common/utils";
import { FormatNumber, formatPrice } from "~/components/common/FormatNumber";
import { PersonName } from "~/components/common/PersonName";
import { Button } from "~/components/ui/button";
import { Layout } from "~/components/ui/layout";
import { Loader } from "~/components/ui/loader";
import { PatientStatusBadge } from "~/components/ui/patient-status-badge";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import { Table, TableCell, TableEmpty, TableHead, TableLoading, TableRow } from "~/components/ui/table";
import { setDocumentTitle } from "~/hooks/use-document-title";
import type { TDoctorPayment } from "~/reducers/dashboard";
import { selectUser } from "~/slices/user";
import type { RootState } from "~/store";
import type { TPatient } from "~/types/patient";

const mapStateToProps = (state: RootState) => {
  return {
    user: selectUser(state),
    pays: state.doctorPays,
    invoice: state.invoice,
  };
};

const mapDispatchToProps = {
  getPays: getDoctorsPays,
  getTasks: getDoctorsTasks,
  doctorPaymentBatch: doctorPaymentBatch,
};

const MenuList = (props) => {
  return <components.MenuList {...props}>{props.children}</components.MenuList>;
};

const customStyles = {
  control: (styles) => ({
    ...styles,
    width: 380,
  }),
  menu: (styles) => {
    return {
      ...styles,
      position: "unset",
    };
  },
};

const animatedComponents = makeAnimated();

type PatientsPayListProps = PropsFromRedux & { intl: IntlShape; bareLayout?: boolean } & RouteComponentProps;

export type PatientsPayListState = {
  pays: TDoctorPayment[];
  paymentState: boolean;
  payment_alert: boolean;
  sort: {
    order: "asc" | "desc";
    name: "patient_id";
  };
  patients: Record<TPatient["patient_id"], { course: { price: number; cbct?: boolean } }>;
  total: number | string;
  showLoader: boolean;
  selectedValues: Record<number, TOption[]>;
  clicked: boolean;
};

type TOption = {
  value: number;
  price: number;
  patient_id: TPatient["patient_id"];
  label: string;
  type: "course" | "extra";
  remain?: number;
  next?: number;
  cbct?: true;
  id?: number;
};

class PatientsPayList extends Component<PatientsPayListProps, PatientsPayListState> {
  constructor(props: PatientsPayListProps) {
    super(props);
    this.state = {
      pays: props.pays.patients,
      paymentState: false,
      payment_alert: false,
      sort: {
        order: "desc",
        name: "patient_id",
      },
      patients: {},
      total: 0,
      showLoader: false,
      selectedValues: {},
      clicked: false,
    };

    this.togglePayment = this.togglePayment.bind(this);
    this.renderPrice = this.renderPrice.bind(this);
    this.fm = this.fm.bind(this);
  }

  componentDidCatch(e: Error) {
    remoteLog(e, "patients_pay_list");
  }

  fm(id: MessageDescriptor["id"]) {
    return this.props.intl.formatMessage({ id });
  }

  componentDidUpdate(prevProps: PatientsPayListProps) {
    if (prevProps.user.account_id && !this.props.user.account_id) {
      this.props.history.push("/");
    }
    if (prevProps.pays.patients !== this.props.pays.patients) {
      const pays = this.props.pays.patients;
      const selectedValues: PatientsPayListState["selectedValues"] = {};
      for (let i = 0; i < pays.length; i++) {
        selectedValues[i] = [];
      }
      this.setState({ pays, selectedValues });
    }
    if (location.protocol === "https:" || isDevelopment) {
      const invoice = this.props.invoice as { link?: string | { formUrl?: string } };
      const formUrl = typeof invoice?.link === "object" && invoice?.link?.formUrl ? invoice.link.formUrl : undefined;
      if (formUrl) {
        redirectTo(formUrl);
      }
    }
  }

  sortedPayments() {
    if (this.state.pays.length < 2) {
      return this.state.pays;
    }

    const sort_order = this.state.sort.order === "asc" ? 1 : -1;
    return this.state.pays.sort((p1, p2) => {
      if (this.state.sort.name === "patient_id") {
        return sort_order * (p1.patient_id - p2.patient_id);
      } else if (this.state.sort.name === "latest_correction") {
        const p1t = this.fm(p1.status);
        const p2t = this.fm(p2.status);
        return sort_order * p1t.localeCompare(p2t);
      } else {
        return sort_order * p1[this.state.sort.name].localeCompare(p2[this.state.sort.name]);
      }
    });
  }

  componentDidMount() {
    this.props.getPays();
    this.props.getTasks();
    window.scrollTo(0, 0);
    setDocumentTitle(this.props.intl.formatMessage({ id: "pat_table_tasks.block_payments" }));
  }

  togglePayment() {
    this.setState({ paymentState: !this.state.paymentState });
  }

  renderPrice(payments: { paid: number; total: number }) {
    if (!payments) {
      return null;
    }

    return (
      <>
        <FormatNumber value={payments.paid} /> {this.fm("pat_table.of")} <FormatNumber value={payments.total} />
      </>
    );
  }

  reformatPaymentsArray(payments: TDoctorPayment["payments"], patient_id: TPatient["patient_id"], course: TDoctorPayment["course"], course_option_tag: TDoctorPayment["course_option_tag"]) {
    const { next, remain, extra, cbct } = payments;
    const mainOptions: TOption[] = [];
    const extraOptions: TOption[] = [];
    if (extra) {
      extra.map((x) => {
        extraOptions.push({
          value: x.id,
          label: `${this.fm(x.tag)} ${formatPrice(x.price)}${this.fm("pat.block.payment.currency.curr")}`,
          price: x.price,
          patient_id,
          id: x.id,
          type: "extra",
        });
      });
    }
    if (next > 0 && remain > 0 && next !== remain) {
      mainOptions.push(
        {
          value: next,
          label: `${this.fm(course)} ${formatPrice(next)} ${this.fm("pat.block.payment.currency.curr")}`,
          next,
          type: "course",
          patient_id,
          price: next,
        },
        {
          value: remain,
          label: `${this.fm("pat.block.payment.pay.all")} ${formatPrice(remain)} ${this.fm("pat.block.payment.currency.curr")}`,
          remain,
          type: "course",
          patient_id,
          price: remain,
        },
      );
    } else if (next === remain && next > 0 && remain > 0) {
      // We introduce a new version of the children course, and it has a different name
      // from the previous one.
      const label = this.fm(course_option_tag == "CHILDREN_V3" ? `C_${course_option_tag}` : course);
      mainOptions.push({
        value: next,
        label: `${label} ${formatPrice(next)} ${this.fm("pat.block.payment.currency.curr")}`,
        next,
        type: "course",
        patient_id,
        price: next,
      });
    }

    if (cbct) {
      mainOptions.map((x) => {
        x.cbct = true;
      });
    }

    return [
      {
        label: this.fm("pat.block.payment.payments"),
        options: mainOptions,
      },
      {
        label: this.fm("pat.block.payment.extras"),
        options: extraOptions,
      },
    ];
  }

  formatPayObject(e: TOption[], pid: TPatient["patient_id"], i: number) {
    this.filterSelect(e, i);
    const patientsObj = {};
    const id = pid;
    e.map((x) => {
      const { cbct } = x;
      if (x.remain && x.type === "course") {
        patientsObj[x.patient_id] = {
          ...patientsObj[x.patient_id],
          ...{ course: { price: x.remain } },
        };
      } else if (x.next && x.type === "course" && !patientsObj[x.patient_id]?.course?.price) {
        patientsObj[x.patient_id] = {
          ...patientsObj[x.patient_id],
          ...{ course: { price: x.next } },
        };
      }

      if (x.cbct) {
        patientsObj[x.patient_id] = {
          ...patientsObj[x.patient_id],
          ...{
            course: {
              ...patientsObj[x.patient_id].course,
              cbct,
            },
          },
        };
      }

      if (patientsObj[x.patient_id]?.extra && x.type === "extra") {
        patientsObj[x.patient_id]["extra"].push({
          price: x.price,
          id: x.id,
        });
      } else if (x.type && x.type === "extra") {
        patientsObj[x.patient_id] = {
          ...patientsObj[x.patient_id],
          ...{
            extra: [
              {
                price: x.price,
                id: x.id,
              },
            ],
          },
        };
      }
    });
    if (e.length === 0) {
      delete this.state.patients[id];
    }
    this.setState({ patients: { ...this.state.patients, ...patientsObj } });
    setTimeout(() => {
      this.setTotal();
    });
  }

  filterSelect(e: TOption[], i: number) {
    let obj = {};
    let valuesArr;
    e.forEach((x) => {
      if (x.remain) {
        obj = { ...obj, remain: x.remain };
      } else if (x.next) {
        obj = { ...obj, next: x.next };
      }
    });

    if (obj.remain && !obj.next) {
      valuesArr = e.filter((x) => x.remain || x.type === "extra");
    } else if (obj.next && !obj.remain) {
      valuesArr = e.filter((x) => x.next || x.type === "extra");
    } else {
      valuesArr = e.filter((x) => x.type === "extra");
    }

    const objectArr = this.state.selectedValues;
    objectArr[i] = valuesArr;

    setTimeout(() => {
      this.setState({ selectedValues: { ...this.state.selectedValues, ...objectArr } });
    });
  }

  setPatientTotal(id) {
    let pricesArr = [];
    let coursePrice = 0;
    let total;

    if (this.state.patients[id] && this.state.patients[id].course) {
      coursePrice = this.state.patients[id].course.price;
    }

    if (this.state.patients[id] && this.state.patients[id].extra) {
      pricesArr = this.state.patients[id].extra.map((x) => x.price);
    }

    pricesArr.push(coursePrice);
    total = pricesArr.reduce((acc, curr) => new Decimal(acc).plus(new Decimal(curr))).toString();

    return formatPrice(parseFloat(total));
  }

  setTotal() {
    const patientsArr = Object.values(this.state.patients);
    const coursePrices = [];
    const extraPrices = [];
    let finalArr;
    let total = 0;
    if (patientsArr.length === 0) {
      this.setState({ total });
    } else {
      patientsArr.forEach((x) => {
        if (x?.course) {
          coursePrices.push(x.course.price);
        }
        if (x?.extra) {
          x.extra.map((x) => {
            extraPrices.push(x.price);
          });
        }
      });

      finalArr = coursePrices.concat(extraPrices);

      if (finalArr.length > 0) {
        total = finalArr.reduce((x, y) => new Decimal(x).plus(new Decimal(y))).toString();
      }
      this.setState({ total: total });
    }
  }

  submitPayment() {
    this.setState({ showLoader: true });
    this.setState({ clicked: true });
    this.props.doctorPaymentBatch(this.state.patients, this.state.total);
  }

  render() {
    const isLoading = !this.props.pays.hasBeenLoaded;
    const showPaymentsPage = window.location.protocol == "https:" || isDevelopment;

    if (!showPaymentsPage) {
      return null;
    }

    return (
      <Layout chrome={!this.props.bareLayout}>
        <Portlet as="main" testId="payments-page">
          <PortletTitle iconClassName="icon-wallet" id="patients-table-block-title" testId="payments-page-title">
            <FormattedMessage id="pat_table_tasks.block_payments" />
          </PortletTitle>

          <div className="portlet-body">
            <div id="doctor_pacients_table_wrapper" className="dataTables_wrapper no-footer">
              <div className="row">
                <div className="col-lg-8 col-md-8 col-sm-8 col-xs-12">
                  <Toggle.Root asChild>
                    <Button
                      id="test-btn"
                      variant={this.state.payment_alert ? "danger" : "default"}
                      // NOTE: minWidth is set to avoid the button size change on toggle because of the text change
                      style={{ marginBottom: 13, minWidth: 152 }}
                      onClick={this.togglePayment}
                      disabled={isLoading}
                    >
                      <FormattedMessage id={this.state.paymentState ? "general.payment.close" : "general.payment.show"} />
                    </Button>
                  </Toggle.Root>
                </div>
              </div>

              <Table id="doctor_pacients_table">
                <thead>
                  <TableRow>
                    <TableHead style={{ width: 14 }}>#</TableHead>

                    <TableHead style={{ width: 110 }}>
                      <FormattedMessage id="pat_table.name" />
                    </TableHead>

                    <TableHead style={{ width: 80 }}>
                      <FormattedMessage id="pat_table.payments" />
                    </TableHead>

                    <TableHead style={{ width: 100 }}>
                      <FormattedMessage id="pat_table.status" />
                    </TableHead>

                    <TableHead style={{ width: 300 }}>
                      <FormattedMessage id="pat_table.services" />
                    </TableHead>

                    <TableHead style={{ width: 60 }}>
                      <FormattedMessage id="pat_table.total" />
                    </TableHead>
                  </TableRow>
                </thead>

                <tbody>
                  {isLoading ? (
                    <TableLoading columns={6} />
                  ) : this.sortedPayments().length == 0 ? (
                    <TableEmpty columns={6}>
                      <FormattedMessage id="pat_table.patients.not_found" />
                    </TableEmpty>
                  ) : (
                    this.sortedPayments().map((patient, i) => {
                      return (
                        <TableRow key={i}>
                          <TableCell className="sorting_1">{patient.patient_id}</TableCell>

                          <TableCell>
                            <NavLink to={`/pages/patient/${patient.patient_id}`}>
                              <PersonName person={patient} />
                            </NavLink>
                          </TableCell>

                          <TableCell>{this.state.paymentState ? this.renderPrice(patient.total_payments) : null}</TableCell>

                          <TableCell>
                            <PatientStatusBadge status={patient.status} />
                          </TableCell>

                          <TableCell data-matomo-mask data-hj-suppress>
                            <Select
                              styles={customStyles}
                              closeMenuOnSelect={false}
                              components={{ animatedComponents, MenuList }}
                              isMulti
                              options={this.reformatPaymentsArray(patient.payments, patient.patient_id, patient.course, patient.course_option_tag)}
                              onChange={(e) => this.formatPayObject(e, patient.patient_id, i)}
                              placeholder={this.fm("select.option")}
                              value={this.state.selectedValues[i]}
                            />
                          </TableCell>

                          <TableCell data-matomo-mask data-hj-suppress>
                            {this.setPatientTotal(patient.patient_id)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </tbody>
              </Table>

              <br />
              <div>
                <b>
                  <FormattedMessage id="payments.doctor.total" />
                </b>{" "}
                {formatPrice(parseFloat(this.state.total))}&nbsp;
                <FormattedMessage id="pat.block.payment.currency.curr" />
              </div>

              <br />

              {PAYMENT_SYSTEM_TYPE == PaymentSystem.SBERBANK ? (
                <div className="form-group">
                  <textarea className="form-control" rows={8} readOnly={true} defaultValue={SBERBANK_DISCLAIMER}></textarea>
                </div>
              ) : null}

              <br />
              <div>
                <Button variant="blue" disabled={this.state.total <= 0 || this.state.clicked} onClick={() => this.submitPayment()}>
                  <FormattedMessage id="pat.payments.buttons.pay" />
                </Button>
              </div>

              {this.state.showLoader ? <Loader msg={this.fm("pat.payments.you.will.be.redirected")} /> : null}
            </div>
          </div>
        </Portlet>
      </Layout>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(PatientsPayList));
